import { useQuery } from '@tanstack/react-query';
import { getAdminProductsByIds, type Product } from './api';

/**
 * Resolve a saved product-id list to rows for display.
 *
 * The admin surfaces that persist an id list (flash sale, featured/landing
 * packages, storefront deals, coupons, a replicated page's CTA) used to label
 * their selection out of the same truncated catalog page they searched. A saved
 * product beyond that page — or one since archived — resolved to `undefined` and
 * was quietly dropped from the UI, while remaining saved and therefore
 * unremovable. This resolves by id instead, archived included, order preserved.
 */
export function useProductsByIds(ids: string[]): {
  products: Product[];
  byId: Map<string, Product>;
  isLoading: boolean;
} {
  // Order-insensitive cache key: reordering a selection must not refetch, and
  // the caller re-orders the result itself from its own id array anyway.
  const key = [...ids].sort().join(',');
  const q = useQuery({
    queryKey: ['admin-products-by-ids', key],
    queryFn: () => getAdminProductsByIds(ids),
    enabled: ids.length > 0,
    staleTime: 30_000,
  });
  const products = q.data ?? [];
  return {
    products,
    byId: new Map(products.map((p) => [p.id, p])),
    isLoading: q.isLoading,
  };
}
